// ═══════════════════════════════════════════════════════════════
// useFilteredNav — filters NavItem[] based on GAB permissions
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { usePermissionsStore } from '../store/permissionsStore';
import type { NavItem } from '../types/nav';

const LEVEL_RANK: Record<string, number> = { none: 0, viewer: 1, editor: 2, admin: 3 };

function hasMinLevel(actual: string, required: string): boolean {
  return (LEVEL_RANK[actual] ?? 0) >= (LEVEL_RANK[required] ?? 0);
}

export function useFilteredNav(moduleId: string, items: NavItem[]): NavItem[] {
  const access  = usePermission(moduleId);
  const isAdmin = usePermissionsStore(s => s.isAdmin);
  const hasRole = usePermissionsStore(s => s.hasRole);

  return useMemo(() => {
    // System admins and EOC directors see everything
    if (isAdmin()) return items;

    return items.filter(item => {
      if (item.minLevel && !hasMinLevel(access.level, item.minLevel)) return false;
      if (item.visibleToSlugs?.length && !item.visibleToSlugs.some(s => hasRole(s))) return false;
      if (item.hiddenFromSlugs?.length &&  item.hiddenFromSlugs.some(s => hasRole(s))) return false;
      return true;
    });
  }, [items, access.level, isAdmin, hasRole]);
}
